/**
 * Shared frontend API client (Synterra Integration Sprint · item A3).
 *
 * ONE place that builds request URLs, attaches auth/session context, and parses
 * responses. Every screen — internal/admin, site portal, or public bootstrap —
 * routes through here so header construction, initiative/site scoping, and
 * error handling stop drifting between the old `adminFetch`, `siteFetch`, and
 * the ~14 raw-`fetch` call sites.
 *
 * Contexts
 *   - 'admin'  : internal/staff surface. Sends the session cookie always, the
 *                break-glass X-Admin-Code when a staff member has entered one,
 *                and the active initiative as BOTH the X-Initiative-Id header
 *                and an `initiative_id` query param. When no active initiative
 *                is set, nothing initiative-related is injected — legacy/default
 *                (`hiv`) initiatives behave exactly as before.
 *   - 'site'   : site-portal surface. Sends the session cookie always and
 *                routes a 401 through the registered session-expired handler
 *                (the single seam that drops the user back to sign-in).
 *   - 'public' : unauthenticated bootstrap calls (access-code / login probes).
 *                Cookie still travels, but no admin/site scoping is attached.
 *
 * Response handling is uniform: safe JSON parsing, empty/`204` bodies handled,
 * and a typed {@link ApiError} that carries the HTTP status plus parsed detail.
 * A 401 still fires the site session-expired handler; 403 / 404 / 400·422
 * validation / network failures each surface as a distinguishable {@link
 * ApiErrorKind} that callers can render.
 */

import { apiUrl, getActiveInitiativeId, getAdminCode, clearAdminCode } from '../config';

export type ApiContext = 'admin' | 'site' | 'public';

/**
 * Distinguishable outcome for a failed request. Callers switch on this to
 * render the right message instead of parsing status codes ad hoc.
 */
export type ApiErrorKind =
    | 'network' // fetch itself threw (offline / DNS / CORS) — status is 0
    | 'unauthorized' // 401
    | 'forbidden' // 403
    | 'not_found' // 404
    | 'validation' // 400 or 422
    | 'rate_limited' // 429
    | 'server' // 5xx
    | 'http'; // any other non-2xx

/** Parse a FastAPI-style error body into a single human-readable string. */
function extractDetail(body: unknown): string | null {
    if (body == null) return null;
    if (typeof body === 'string') return body.trim() || null;
    if (typeof body === 'object') {
        const b = body as Record<string, unknown>;
        const detail = b.detail;
        if (typeof detail === 'string') return detail.trim() || null;
        // FastAPI validation errors: detail is an array of { loc, msg, type }.
        if (Array.isArray(detail)) {
            const msgs = detail
                .map((d) =>
                    d && typeof d === 'object' && typeof (d as Record<string, unknown>).msg === 'string'
                        ? String((d as Record<string, unknown>).msg)
                        : null,
                )
                .filter((m): m is string => !!m);
            if (msgs.length) return msgs.join('; ');
        }
        if (typeof b.message === 'string') return b.message.trim() || null;
    }
    return null;
}

function kindForStatus(status: number): ApiErrorKind {
    if (status === 401) return 'unauthorized';
    if (status === 403) return 'forbidden';
    if (status === 404) return 'not_found';
    if (status === 400 || status === 422) return 'validation';
    if (status === 429) return 'rate_limited';
    if (status >= 500) return 'server';
    return 'http';
}

/**
 * Typed error thrown by {@link apiRequest} / {@link api}. Carries the HTTP
 * status (0 for network failures), the parsed {@link ApiErrorKind}, a
 * human-readable `detail` (when the server provided one), and the full parsed
 * `body` for callers that need more.
 */
export class ApiError extends Error {
    readonly status: number;
    readonly kind: ApiErrorKind;
    readonly detail: string | null;
    readonly body: unknown;

    constructor(kind: ApiErrorKind, status: number, detail: string | null, body: unknown) {
        super(detail || `${status || ''} ${kind}`.trim());
        this.name = 'ApiError';
        this.kind = kind;
        this.status = status;
        this.detail = detail;
        this.body = body;
        // Restore prototype chain for `instanceof` under transpiled targets.
        Object.setPrototypeOf(this, ApiError.prototype);
    }

    static fromResponse(status: number, body: unknown): ApiError {
        return new ApiError(kindForStatus(status), status, extractDetail(body), body);
    }

    static network(cause?: unknown): ApiError {
        return new ApiError('network', 0, null, cause ?? null);
    }

    get isNetwork(): boolean {
        return this.kind === 'network';
    }
    get isUnauthorized(): boolean {
        return this.kind === 'unauthorized';
    }
    get isForbidden(): boolean {
        return this.kind === 'forbidden';
    }
    get isNotFound(): boolean {
        return this.kind === 'not_found';
    }
    get isValidation(): boolean {
        return this.kind === 'validation';
    }
    get isRateLimited(): boolean {
        return this.kind === 'rate_limited';
    }
}

// ─── Site session-expired seam ──────────────────────────────────────────────
// The cookie-based site session can expire mid-use (12 h sliding, 7 d hard cap)
// or be revoked from another tab. The root SitePortal registers a handler so a
// 401 from ANY site-context call drops the user back to sign-in with a friendly
// notice. Kept here (not in siteAuth) so the client owns the single 401 seam
// and `siteAuth` can delegate without a circular import.

type SiteSessionExpiredHandler = () => void;
let siteSessionExpiredHandler: SiteSessionExpiredHandler | null = null;

/**
 * Register (or clear, by passing `null`) the callback invoked whenever a
 * site-context request observes a 401. The root portal component sets this
 * only while authenticated, so the initial `/site-users/me` probe (which
 * legitimately 401s for fresh visitors) does not trigger a spurious notice.
 */
export function registerSiteSessionExpiredHandler(cb: SiteSessionExpiredHandler | null): void {
    siteSessionExpiredHandler = cb;
}

function notifySiteSessionExpired(): void {
    if (siteSessionExpiredHandler) {
        try {
            siteSessionExpiredHandler();
        } catch {
            /* never let the handler break the caller */
        }
    }
}

// ─── Request options ────────────────────────────────────────────────────────

export interface ApiRequestInit extends Omit<RequestInit, 'body'> {
    /** Auth/scoping mode. Defaults to `'public'`. */
    context?: ApiContext;
    /**
     * Request body. A plain object/array is auto-serialized to JSON with a
     * matching Content-Type; strings / FormData / Blob / etc. pass through
     * untouched (FormData clears Content-Type so the browser sets the boundary).
     */
    body?: BodyInit | Record<string, unknown> | unknown[] | null;
    /** Convenience: value to JSON-serialize as the body. */
    json?: unknown;
    /** Override the active initiative (admin context only). */
    initiativeId?: string | null;
    /** Attach the system-library curator code (X-System-Library-Code). */
    systemLibraryCode?: string;
    /** Skip the admin `initiative_id` query-param injection. Default: inject. */
    injectInitiativeParam?: boolean;
    /**
     * Treat `path` as an already-resolved absolute URL (skip apiUrl + initiative
     * param). Used by the backward-compatible `siteFetch` wrapper whose callers
     * pass `apiUrl(...)` themselves.
     */
    absoluteUrl?: boolean;
}

function hasHeader(headers: Record<string, string>, name: string): boolean {
    const lower = name.toLowerCase();
    return Object.keys(headers).some((k) => k.toLowerCase() === lower);
}

function normalizeHeaders(init?: HeadersInit): Record<string, string> {
    const out: Record<string, string> = {};
    if (!init) return out;
    if (init instanceof Headers) {
        init.forEach((v, k) => {
            out[k] = v;
        });
    } else if (Array.isArray(init)) {
        for (const [k, v] of init) out[k] = v;
    } else {
        Object.assign(out, init);
    }
    return out;
}

/** Plain objects/arrays get JSON-serialized; everything else is a real BodyInit. */
function isJsonBody(body: unknown): boolean {
    if (body == null) return false;
    if (typeof body === 'string') return false;
    if (body instanceof FormData) return false;
    if (body instanceof Blob) return false;
    if (body instanceof ArrayBuffer) return false;
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) return false;
    if (typeof ReadableStream !== 'undefined' && body instanceof ReadableStream) return false;
    return typeof body === 'object';
}

function injectInitiativeParam(url: string, override?: string | null): string {
    const id = override !== undefined ? override : getActiveInitiativeId();
    if (!id) return url;
    if (/[?&]initiative_id=/.test(url)) return url;
    const hasQuery = url.includes('?');
    return `${url}${hasQuery ? '&' : '?'}initiative_id=${encodeURIComponent(id)}`;
}

// ─── Core fetch (returns the raw Response) ──────────────────────────────────

/**
 * Low-level shared fetch. Builds the URL + headers for the given context and
 * returns the raw `Response` (routing a site-context 401 through the
 * session-expired handler first). Prefer {@link apiRequest} / {@link api} for
 * parsed, typed results; use this only when you need the `Response` directly
 * (this is what the backward-compatible `adminFetch` / `siteFetch` delegate to).
 */
export async function apiFetch(path: string, init: ApiRequestInit = {}): Promise<Response> {
    const {
        context = 'public',
        json,
        initiativeId,
        systemLibraryCode,
        injectInitiativeParam: doInjectParam = true,
        absoluteUrl = false,
        body: rawBody,
        headers: rawHeaders,
        ...rest
    } = init;

    // ── Body serialization ──
    let body: BodyInit | null | undefined;
    let bodyIsJson = false;
    if (json !== undefined) {
        body = JSON.stringify(json);
        bodyIsJson = true;
    } else if (isJsonBody(rawBody)) {
        body = JSON.stringify(rawBody);
        bodyIsJson = true;
    } else {
        body = rawBody as BodyInit | null | undefined;
    }
    const isForm = body instanceof FormData;

    // ── Headers ──
    const headers = normalizeHeaders(rawHeaders);
    if (!isForm) {
        const wantsJson = bodyIsJson || context === 'admin';
        if (wantsJson && !hasHeader(headers, 'content-type')) {
            headers['Content-Type'] = 'application/json';
        }
    }
    if (context === 'admin') {
        const code = getAdminCode();
        // ROLE-BASED ACCESS (#162): only attach X-Admin-Code when a staff member
        // has actually entered one — normal staff access rides the session
        // cookie. A blank header would read as a (blank, invalid) admin attempt.
        if (code && !hasHeader(headers, 'x-admin-code')) {
            headers['X-Admin-Code'] = code;
        }
        const initId = initiativeId !== undefined ? initiativeId : getActiveInitiativeId();
        if (initId && !hasHeader(headers, 'x-initiative-id')) {
            headers['X-Initiative-Id'] = initId;
        }
    }
    if (systemLibraryCode && !hasHeader(headers, 'x-system-library-code')) {
        headers['X-System-Library-Code'] = systemLibraryCode;
    }
    if (isForm) {
        // Let the browser set multipart Content-Type (with boundary) itself.
        for (const k of Object.keys(headers)) {
            if (k.toLowerCase() === 'content-type') delete headers[k];
        }
    }

    // ── URL ──
    let url: string;
    if (absoluteUrl) {
        url = path;
    } else {
        url = apiUrl(path);
        if (context === 'admin' && doInjectParam) {
            url = injectInitiativeParam(url, initiativeId);
        }
    }

    // credentials:'include' always — the session cookie is the primary auth path
    // for both admin (staff session) and site surfaces.
    const resp = await fetch(url, { credentials: 'include', ...rest, headers, body });

    if (resp.status === 401 && context === 'site') {
        notifySiteSessionExpired();
    }
    // False-admin-state guard: if an admin-context request carried a stored
    // break-glass X-Admin-Code and the backend rejected the CREDENTIAL itself
    // (401 invalid, 503 unconfigured), the stored code is definitively bad —
    // clear it so the "Admin" chip relocks instead of lying. 403s are left
    // alone (role/permission denials, not credential failures).
    // Exception: when the request ALSO carried a secondary credential (the
    // Measure Library's X-System-Library-Code), a 401/503 most likely rejects
    // THAT credential (the admin guard runs first server-side, so an invalid
    // admin code would have been caught regardless) — leave the admin code
    // alone rather than conflating the two.
    if (
        context === 'admin' &&
        (resp.status === 401 || resp.status === 503) &&
        hasHeader(headers, 'x-admin-code') &&
        !hasHeader(headers, 'x-system-library-code')
    ) {
        clearAdminCode();
    }
    return resp;
}

// ─── Parsed, typed request ──────────────────────────────────────────────────

/** Safe body parse: empty/`204` → null; JSON when possible; else raw text. */
async function parseBody(resp: Response): Promise<unknown> {
    if (resp.status === 204) return null;
    let text: string;
    try {
        text = await resp.text();
    } catch {
        return null;
    }
    if (!text) return null;
    try {
        return JSON.parse(text);
    } catch {
        return text;
    }
}

/**
 * Perform a request and return the parsed body (typed as `T`). Empty/`204`
 * responses resolve to `null`. On a non-2xx response or a network failure this
 * throws an {@link ApiError}; a site-context 401 also fires the session-expired
 * handler before throwing.
 */
export async function apiRequest<T = unknown>(path: string, init: ApiRequestInit = {}): Promise<T> {
    let resp: Response;
    try {
        resp = await apiFetch(path, init);
    } catch (e) {
        throw ApiError.network(e);
    }
    const body = await parseBody(resp);
    if (!resp.ok) {
        throw ApiError.fromResponse(resp.status, body);
    }
    return body as T;
}

// ─── Convenience verbs ──────────────────────────────────────────────────────

type VerbInit = Omit<ApiRequestInit, 'method' | 'json' | 'body'>;

export const api = {
    request: apiRequest,
    fetch: apiFetch,
    get: <T = unknown>(path: string, init: VerbInit = {}) =>
        apiRequest<T>(path, { ...init, method: 'GET' }),
    post: <T = unknown>(path: string, body?: unknown, init: VerbInit = {}) =>
        apiRequest<T>(path, { ...init, method: 'POST', json: body }),
    put: <T = unknown>(path: string, body?: unknown, init: VerbInit = {}) =>
        apiRequest<T>(path, { ...init, method: 'PUT', json: body }),
    patch: <T = unknown>(path: string, body?: unknown, init: VerbInit = {}) =>
        apiRequest<T>(path, { ...init, method: 'PATCH', json: body }),
    del: <T = unknown>(path: string, init: VerbInit = {}) =>
        apiRequest<T>(path, { ...init, method: 'DELETE' }),
};

// ─── Dashboard load-failure classification ──────────────────────────────────
// A single source of truth every dashboard uses to decide WHY a load produced
// no live data, so the on-screen label is honest and consistent:
//   • 'offline' — backend truly unreachable: network failure / status 0 / 5xx.
//   • 'auth'    — reachable but access-gated (needs sign-in): 401 / 403 / 422.
// `null` means neither applies (a reachable 2xx / empty response, or a status
// that is not a recognised failure) — i.e. treat it as an honest empty state.

export type DashboardLoadFailure = 'offline' | 'auth';

/** Classify a single HTTP status (0 = network-level failure). */
export function classifyFailureStatus(status: number): DashboardLoadFailure | null {
    if (status === 0 || status >= 500) return 'offline';
    if (status === 401 || status === 403 || status === 422) return 'auth';
    return null;
}

/**
 * Classify a set of statuses from a fan-out load. Offline wins over auth: if
 * ANY call was network/5xx the backend is (partly) unreachable; otherwise if
 * any call was access-gated the viewer needs to sign in.
 */
export function classifyFailureStatuses(statuses: number[]): DashboardLoadFailure | null {
    if (statuses.some((s) => s === 0 || s >= 500)) return 'offline';
    if (statuses.some((s) => s === 401 || s === 403 || s === 422)) return 'auth';
    return null;
}

/** Classify a thrown {@link ApiError} (from `api.*`/`apiRequest`). */
export function classifyApiError(err: unknown): DashboardLoadFailure | null {
    if (err instanceof ApiError) {
        if (err.kind === 'network' || err.kind === 'server') return 'offline';
        if (err.kind === 'unauthorized' || err.kind === 'forbidden' || err.kind === 'validation') {
            return 'auth';
        }
    }
    return null;
}

/** Map any thrown error into a user-facing message for a standardized banner. */
export function describeApiError(err: unknown): string {
    if (err instanceof ApiError) {
        if (err.isNetwork) return 'Network error — please check your connection and try again.';
        if (err.detail) return err.detail;
        switch (err.kind) {
            case 'unauthorized':
                return 'Your session has expired. Please sign in again.';
            case 'forbidden':
                return 'You do not have access to this resource.';
            case 'not_found':
                return 'The requested resource was not found.';
            case 'validation':
                return 'The request was invalid. Please check your input and try again.';
            case 'rate_limited':
                return 'Too many attempts. Please wait a few minutes and try again.';
            case 'server':
                return 'The server encountered an error. Please try again shortly.';
            default:
                return `Request failed (${err.status}).`;
        }
    }
    return 'Something went wrong. Please try again.';
}
